import { Component, Inject, TemplateRef, ViewChild } from "@angular/core";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog
} from "@angular/material/dialog";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from "@angular/forms";
import { environment } from "../../../../../environments/environment";
import { Tenant } from "../../../models/tenant.model";
import { Subject, of, Observable, from } from "rxjs";
import { MasterTenantsService } from "../../../services/master-tenants.service";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  filter,
  map,
  catchError
} from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { DeviceRegistrationService } from "../../../../core/services/device-registration.service";
import { AccountService } from "../../../../core";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { I18nService } from "../../../services";
import { PropertyService } from "../../../services/property.services";

@Component({
  selector: "app-edit-tenants",
  templateUrl: "./edit-tenants.component.html",
  styleUrls: ["./edit-tenants.component.scss"]
})
export class EditTenantsComponent {
  tenantForm: FormGroup;
  isCreating: boolean;
  countries;
  industries;
  tenants: any;
  editingIndex: any;
  errorMessage = false;
  platformId = environment.platformId;
  parentCorporationList = ["ALL", "OFFICE_INDUSTRIAL", "RETAIL"];
  tenantsList: Tenant[] = [];
  filterName = null;
  filterIndustry: number;
  filterCountry: string;
  filterStatus: boolean;
  filterDate: number;
  filterContact: boolean;
  page = 1;
  perPage = 20;
  private searchSubject = new Subject<string>();
  private mergeCorporationIdSubject = new Subject<number>();
  isLoading = false;
  isDropdownOpen = false;
  dropdownClicked = false;
  industryRetails;
  akaError: boolean = false;
  corporation = null;
  isAdmin = false;
  isResearchAdmin = false;
  isCountryManager = false;
  isResearcher = false;
  user: any;
  i18n: I18nService;
  @ViewChild("mergeCorporationModal") mergeCorporationModal: TemplateRef<any>;
  mergeCorporationId: number = null;
  dialogRefMergeCorporation: MatDialogRef<any>;
  mergeCorporationResult: { id: number; name: string } | null = null;

  constructor(
    public dialogRef: MatDialogRef<EditTenantsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      tenant: any;
      create: boolean;
      industries: any;
      countries: any;
      industryRetails: any;
    },
    private fb: FormBuilder,
    private masterTenantsService: MasterTenantsService,
    private i18: I18nService,
    private dialog: MatDialog,
    private propertyService: PropertyService,
    private snackBar: MatSnackBar
  ) {
    this.i18n = i18;
    this.isCreating = data.create;
    this.industries = data.industries;
    this.countries = data.countries;
    this.industryRetails = data.industryRetails;
    this.populateCorporationDetails(this.data?.tenant?.id);
    let user = JSON.parse(localStorage.getItem("user"));
    this.user = user;
    this.isAdmin =
      user?.roles?.includes("ROLE_RESEARCH_LEAD") ||
      user?.roles?.includes("ROLE_COUNTRY_MANAGER");
    this.isResearcher =
      user?.roles?.includes("ROLE_RESEARCH") &&
      !user?.roles?.includes("ROLE_RESEARCH_LEAD") &&
      !user?.roles?.includes("ROLE_COUNTRY_MANAGER");
    this.isCountryManager = user?.roles?.includes("ROLE_COUNTRY_MANAGER");
    this.initTenantForm();
  }

  get parentCorporationNameValue(): string {
    const value = this.tenantForm.get("parentCorporation")?.value;
    return typeof value === "object" && value?.name ? value.name : "";
  }

  ngOnInit(): void {
    this.tenantForm.patchValue(this.data?.tenant);

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          this.isLoading = true;
          return this.masterTenantsService.getMasterTenants(
            this.page - 1,
            this.perPage,
            term ? term : null,
            this.filterIndustry,
            this.filterCountry,
            this.filterDate,
            this.filterStatus,
            this.filterContact
          );
        })
      )
      .subscribe((results: any) => {
        this.tenantsList = results.content;
        this.isLoading = false;
      });

    this.tenantForm.get("aka")?.valueChanges.subscribe(() => {
      this.akaError = false;
    });
    this.tenantForm.get("name")?.valueChanges.subscribe(() => {
      this.akaError = false;
    });

    // Setup debounce for merge corporation ID input
    this.mergeCorporationIdSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((id: number) => id !== null && id > 0),
        switchMap((id: number) => {
          return this.fetchMergeCorporationById(id);
        })
      )
      .subscribe((result: { id: number; name: string } | null) => {
        // Only set result if it matches the current input value
        if (result && result.id === this.mergeCorporationId) {
          this.mergeCorporationResult = result;
        } else {
          this.mergeCorporationResult = null;
        }
      });
  }

  save() {
    if (this.tenantForm.valid) {
      const name = this.tenantForm.value.name?.toLowerCase().trim();
      const aka = this.tenantForm.value.aka?.toLowerCase().trim();

      if (
        name &&
        aka &&
        (name === aka || name.includes(aka) || aka.includes(name))
      ) {
        this.akaError = true;
        return;
      }

      let formValue = { ...this.tenantForm.value };

      if (formValue.parentCorporation?.name === formValue.name) {
        formValue.parentCorporation = null;
      }
      formValue.action = "saveTenant";

      this.dialogRef.close(formValue);
    } else {
      this.errorMessage = true;
    }
  }

  close() {
    this.dialogRef.close();
  }

  populateCorporationDetails(corporationId) {
    if (corporationId > 0) {
      this.masterTenantsService
        .getCorporationDetail(corporationId)
        .then((resp: any) => {
          this.corporation = resp;
        });
    }
  }

  initTenantForm() {
    this.masterTenantsService;

    this.tenantForm = this.fb.group({
      id: new FormControl(),
      name: new FormControl("", Validators.required),
      industry: new FormControl(
        null,
        this.platformId == "mx" ? null : Validators.required
      ),
      country: new FormControl(
        "",
        this.platformId == "mx" ? Validators.required : {}
      ),
      contactUrl: new FormControl(""),
      contactName: new FormControl(""),
      contactPhone: new FormControl(""),
      contactEmail: new FormControl("", Validators.email),
      tenant: new FormControl(true),
      ...(!this.isCreating ? { createdDate: new FormControl("") } : {}),
      aka: new FormControl(""),
      status: new FormControl(""),
      parentCorporation: new FormControl(null),
      ...(this.platformId == "mx"
        ? {
            corporationPropertyType: new FormControl(
              this.isCreating ? "ALL" : ""
            )
          }
        : {}),
      ...(this.platformId == "mx"
        ? { industryRetail: new FormControl(null) }
        : {})
    });

    if (this.platformId == "mx") {
      this.tenantForm
        .get("corporationPropertyType")
        ?.valueChanges.subscribe(value => {
          if (value === "RETAIL") {
            this.tenantForm
              .get("industryRetail")
              ?.setValidators(Validators.required);
            this.tenantForm.get("industry")?.clearValidators();
          }

          if (value === "OFFICE_INDUSTRIAL") {
            this.tenantForm.get("industryRetail")?.clearValidators();
            this.tenantForm.get("industry")?.setValidators(Validators.required);
          }

          if (value === "ALL") {
            this.tenantForm
              .get("industryRetail")
              ?.setValidators(Validators.required);
            this.tenantForm.get("industry")?.setValidators(Validators.required);
          }

          this.tenantForm.get("industry")?.updateValueAndValidity();
          this.tenantForm.get("industryRetail")?.updateValueAndValidity();
        });
    }
  }

  toggleActivation() {
    let updatedTenant: Tenant;
    updatedTenant = {} as Tenant;
    updatedTenant.action = "Toggle Activation";
    if (this.corporation?.status === "Active") {
      this.masterTenantsService
        .deactivateCorporation(this.corporation.id)
        .then(() => {
          this.corporation.status = "Inactive";
          this.tenantForm.get("status")?.setValue("Inactive");
          this.save();
        })
        .catch(error => {
          updatedTenant.action = "error deactivating";
          this.dialogRef.close(updatedTenant);
        });
    } else {
      this.masterTenantsService
        .activateCorporation(this.corporation.id)
        .then(() => {
          this.corporation.status = "Active";
          this.tenantForm.get("status")?.setValue("Active");
          this.save();
        });
    }
  }

  disableTenant() {
    let updatedTenant: Tenant;
    updatedTenant = {} as Tenant;
    updatedTenant.action = "Toggle Activation";
    this.masterTenantsService
      .deactivateCorporation(this.corporation.id)
      .then(() => {
        this.corporation.status = "Inactive";
        this.tenantForm.get("status")?.setValue("Inactive");
        this.save();
      })
      .catch(error => {
        updatedTenant.action = "error deactivating";
        this.dialogRef.close(updatedTenant);
      });
  }

  compareIndustries(ind1: any, ind2: any): boolean {
    return ind1 && ind2 ? ind1.id === ind2.id : ind1 === ind2;
  }

  onInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchSubject.next(input);
    this.isDropdownOpen = true;
  }

  removeDots(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\./g, "");
    this.tenantForm.get("name")?.setValue(input.value);
  }

  onSelectOption(option: any): void {
    if (this.tenantForm.controls.name != option.name) {
      this.tenantForm.get("parentCorporation")?.setValue(option);
    }
    this.tenantsList = [];
    this.isDropdownOpen = false;
  }

  validateCorporationProprerty() {
    return (
      this.tenantForm?.get("corporationPropertyType")?.value ===
      "OFFICE_INDUSTRIAL"
    );
  }

  validateRetail() {
    return this.tenantForm?.get("corporationPropertyType")?.value === "RETAIL";
  }

  openMergeCorporationModal(): void {
    this.mergeCorporationId = null;
    this.mergeCorporationResult = null;
    this.dialogRefMergeCorporation = this.dialog.open(
      this.mergeCorporationModal,
      {
        width: "500px"
      }
    );

    this.dialogRefMergeCorporation.afterClosed().subscribe(result => {
      this.mergeCorporationResult = null;
    });
  }

  closeMergeCorporationModal(): void {
    this.dialogRefMergeCorporation.close();
  }

  onMergeCorporationIdChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value?.trim();

    // Always clear result immediately when user types
    this.mergeCorporationResult = null;

    if (value && !isNaN(Number(value))) {
      const numValue = parseInt(value, 10);
      if (numValue > 0) {
        this.mergeCorporationId = numValue;
        this.mergeCorporationIdSubject.next(numValue);
      } else {
        this.mergeCorporationId = null;
      }
    } else {
      // Input is empty - clear everything
      this.mergeCorporationId = null;
      this.mergeCorporationResult = null;
    }
  }

  fetchMergeCorporationById(
    id: number
  ): Observable<{ id: number; name: string } | null> {
    // Convert Promise to Observable and map the response to the expected format
    return from(this.masterTenantsService.getCorporationDetail(id)).pipe(
      map((resp: any) => {
        return {
          id: resp.id,
          name: resp.name
        };
      }),
      catchError(error => {
        // Return null if API call fails
        return of(null);
      })
    );
  }

  mergeCorporation(): void {
    this.propertyService
      .mergeCorporation(this.corporation.id, this.mergeCorporationId)
      .then(result => {
        this.snackBar.open(
          this.i18.get("master-tenant.mergeCorporationSuccessfully"),
          "Close",
          {
            duration: 3000, // Duration in milliseconds
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: ["custom-snackbar"]
          }
        );
        this.dialogRefMergeCorporation.close();
        let updatedTenant: Tenant;
        updatedTenant = {} as Tenant;
        updatedTenant.action = "mergeCorporation";
        this.dialogRef.close(updatedTenant);
      })
      .catch(error => {
        this.snackBar.open(
          this.i18.get("master-tenant.mergeCorporationError"),
          "Close",
          {
            duration: 3000,
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: ["custom-snackbar"]
          }
        );
      });
  }
}
